import { z } from 'zod';
import {
  AssistantReportDefinitionSchema,
  AssistantReportExportFormatSchema,
  AssistantReportPreviewSchema,
  AssistantWorkflowApprovalStatusSchema,
  AssistantWorkflowConnectorBindingSchema,
  AssistantWorkflowDatasourceSelectionPlanSchema,
  AssistantWorkflowExecutionStatusSchema,
  AssistantWorkflowGraphSpecSchema,
  AssistantWorkflowReviewDecisionSchema,
  AssistantWorkflowTemplateIdSchema,
  AssistantWorkflowTriggerConfigSchema,
  AssistantWorkflowTriggerKindSchema,
  AssistantPromptPayloadSchema,
  IntellioAssistantConversationMessageSourceSchema,
  IntellioAssistantConversationOriginRefSchema,
  IntellioAssistantConversationSessionStatusSchema,
  IntellioAssistantConversationSourceAuthoritySchema,
  IntellioAssistantArtifactRefSchema,
  IntellioAssistantModuleSchema,
  IntellioAssistantReportScheduleSchema,
  IntellioAssistantReportTemplateSchema,
  IntellioAssistantRunStateSchema,
  IntellioAssistantSnoozePresetSchema,
  IntellioAssistantTaskClassSchema,
  IntellioAssistantTaskPhaseSchema,
  IntellioAssistantTenantScopeStatusSchema,
  IntellioAssistantUrgencySchema,
  IntellioAssistantWorkStateSchema,
  IntellioInterventionPrioritySchema,
  IntellioProactiveChannelSchema,
  IntellioRoutingHintsSchema,
} from '../entities/assistant';
import { UnixTimestamp } from '../types/branded';
import { JsonArraySchema, JsonObjectSchema } from '../types/json';
import { OptionalNonEmptyStringInput, optionalNullishInput } from '../types/zod-inputs';
import { AssistantActiveRouteContextEnvelopeSchema } from './assistant-active-route-contract';

export type AppSyncJsonArgument<TValue> = TValue | string;

export const IntellioAssistantWorkItemRecordSchema = z.object({
  id: z.string(),
  actorId: z.string(),
  actorRole: z.string(),
  tenantId: z.string(),
  tenantScopeStatus: IntellioAssistantTenantScopeStatusSchema.optional(),
  state: IntellioAssistantWorkStateSchema,
  urgency: IntellioAssistantUrgencySchema,
  module: IntellioAssistantModuleSchema,
  route: z.string().trim().min(1),
  routeScore: z.number().int().nullable().optional(),
  title: z.string(),
  summary: z.string().nullable().optional(),
  sourceRef: z.string().nullable().optional(),
  runId: z.string().nullable().optional(),
  workflowKey: z.string().nullable().optional(),
  workflowDefinitionId: z.string().nullable().optional(),
  workflowExecutionId: z.string().nullable().optional(),
  executionSnapshotHash: z.string().nullable().optional(),
  taskClass: IntellioAssistantTaskClassSchema.nullable().optional(),
  statusMessage: z.string().nullable().optional(),
  phaseKey: IntellioAssistantTaskPhaseSchema.nullable().optional(),
  phaseLabel: z.string().nullable().optional(),
  progressPercent: z.number().int().min(0).max(100).nullable().optional(),
  cancelable: z.boolean().optional(),
  launchSource: z.enum(['prompt', 'action', 'operator']).nullable().optional(),
  resultSummary: z.string().nullable().optional(),
  artifactRefs: z.array(IntellioAssistantArtifactRefSchema).optional(),
  origin: IntellioAssistantConversationOriginRefSchema.nullable().optional(),
  traceId: z.string().nullable().optional(),
  notificationId: z.string().nullable().optional(),
  assistantPrompt: AssistantPromptPayloadSchema.nullable().optional(),
  nextVisibleAt: UnixTimestamp.nullable().optional(),
  expiresAt: UnixTimestamp.nullable().optional(),
  createdAt: UnixTimestamp,
  updatedAt: UnixTimestamp,
}).strict();
export type IntellioAssistantWorkItemRecord = z.infer<typeof IntellioAssistantWorkItemRecordSchema>;

export const IntellioAssistantRunRecordSchema = z.object({
  id: z.string(),
  actorId: z.string(),
  actorRole: z.string(),
  tenantId: z.string(),
  tenantScopeStatus: IntellioAssistantTenantScopeStatusSchema.optional(),
  state: IntellioAssistantRunStateSchema,
  module: IntellioAssistantModuleSchema,
  route: z.string().trim().min(1),
  question: z.string(),
  workItemId: z.string().nullable().optional(),
  workflowKey: z.string().nullable().optional(),
  workflowDefinitionId: z.string().nullable().optional(),
  workflowExecutionId: z.string().nullable().optional(),
  executionSnapshotHash: z.string().nullable().optional(),
  origin: IntellioAssistantConversationOriginRefSchema.nullable().optional(),
  traceId: z.string().nullable().optional(),
  answer: z.string().nullable().optional(),
  response: JsonObjectSchema.nullable().optional(),
  errorMessage: z.string().nullable().optional(),
  createdAt: UnixTimestamp,
  startedAt: UnixTimestamp.nullable().optional(),
  completedAt: UnixTimestamp.nullable().optional(),
  updatedAt: UnixTimestamp,
}).strict();
export type IntellioAssistantRunRecord = z.infer<typeof IntellioAssistantRunRecordSchema>;

export const IntellioAssistantConversationSessionRecordSchema = z.object({
  id: z.string(),
  actorId: z.string(),
  actorRole: z.string(),
  tenantId: z.string(),
  tenantScopeStatus: IntellioAssistantTenantScopeStatusSchema.optional(),
  status: IntellioAssistantConversationSessionStatusSchema,
  module: IntellioAssistantModuleSchema,
  route: z.string().trim().min(1),
  persona: z.string().nullable().optional(),
  title: z.string().nullable().optional(),
  routeLabel: z.string().nullable().optional(),
  firstUserMessageId: z.string().nullable().optional(),
  firstUserPreview: z.string().nullable().optional(),
  lastMessageId: z.string().nullable().optional(),
  lastMessagePreview: z.string().nullable().optional(),
  lastAssistantMessageId: z.string().nullable().optional(),
  lastAssistantPreview: z.string().nullable().optional(),
  messageCount: z.number().int().nonnegative(),
  sourceAuthority: IntellioAssistantConversationSourceAuthoritySchema.optional(),
  startedAt: UnixTimestamp,
  updatedAt: UnixTimestamp,
  closedAt: UnixTimestamp.nullable().optional(),
}).strict();
export type IntellioAssistantConversationSessionRecord = z.infer<typeof IntellioAssistantConversationSessionRecordSchema>;

export const IntellioAssistantConversationMessageRecordSchema = z.object({
  sessionId: z.string(),
  messageId: z.string(),
  sequence: z.number().int().nonnegative(),
  role: z.string().trim().min(1),
  content: z.string(),
  citations: JsonArraySchema.nullable().optional(),
  actions: JsonArraySchema.nullable().optional(),
  metadata: JsonObjectSchema.nullable().optional(),
  source: IntellioAssistantConversationMessageSourceSchema.optional(),
  threadId: z.string().nullable().optional(),
  runId: z.string().nullable().optional(),
  workItemId: z.string().nullable().optional(),
  createdAt: UnixTimestamp,
}).strict();
export type IntellioAssistantConversationMessageRecord = z.infer<typeof IntellioAssistantConversationMessageRecordSchema>;

export const IntellioAssistantConversationSessionQueryInputSchema = z.object({
  actorId: OptionalNonEmptyStringInput,
  includeClosed: optionalNullishInput(z.boolean()),
  limit: optionalNullishInput(z.number().int().positive().max(100)),
  nextToken: OptionalNonEmptyStringInput,
}).strict();
export type IntellioAssistantConversationSessionQueryInput = z.infer<typeof IntellioAssistantConversationSessionQueryInputSchema>;

export const IntellioAssistantConversationSessionQueryArgumentsSchema = z.object({
  input: z.union([IntellioAssistantConversationSessionQueryInputSchema, z.string()]).optional(),
}).strict();
export type IntellioAssistantConversationSessionQueryArguments = z.infer<typeof IntellioAssistantConversationSessionQueryArgumentsSchema>;

export const IntellioAssistantConversationSessionConnectionSchema = z.object({
  items: z.array(IntellioAssistantConversationSessionRecordSchema),
  totalCount: z.number().int().nonnegative(),
  nextToken: z.string().nullable().optional(),
}).strict();
export type IntellioAssistantConversationSessionConnection = z.infer<typeof IntellioAssistantConversationSessionConnectionSchema>;

export const IntellioAssistantConversationTimelineArgumentsSchema = z.object({
  sessionId: z.string().trim().min(1),
}).strict();
export type IntellioAssistantConversationTimelineArguments = z.infer<typeof IntellioAssistantConversationTimelineArgumentsSchema>;

export const IntellioAssistantConversationTimelineOutputSchema = z.object({
  session: IntellioAssistantConversationSessionRecordSchema,
  messages: z.array(IntellioAssistantConversationMessageRecordSchema),
}).strict();
export type IntellioAssistantConversationTimelineOutput = z.infer<typeof IntellioAssistantConversationTimelineOutputSchema>;

export const IntellioStartAssistantConversationSessionInputSchema = z.object({
  actorId: OptionalNonEmptyStringInput,
  pathname: z.string().trim().min(1),
  module: IntellioAssistantModuleSchema,
  persona: OptionalNonEmptyStringInput,
  title: OptionalNonEmptyStringInput,
  routeLabel: OptionalNonEmptyStringInput,
}).strict();
export type IntellioStartAssistantConversationSessionInput = z.infer<typeof IntellioStartAssistantConversationSessionInputSchema>;

export const IntellioStartAssistantConversationSessionArgumentsSchema = z.object({
  input: z.union([IntellioStartAssistantConversationSessionInputSchema, z.string()]),
}).strict();
export type IntellioStartAssistantConversationSessionArguments = z.infer<typeof IntellioStartAssistantConversationSessionArgumentsSchema>;

export const IntellioCloseAssistantConversationSessionInputSchema = z.object({
  actorId: OptionalNonEmptyStringInput,
  sessionId: z.string().trim().min(1),
}).strict();
export type IntellioCloseAssistantConversationSessionInput = z.infer<typeof IntellioCloseAssistantConversationSessionInputSchema>;

export const IntellioCloseAssistantConversationSessionArgumentsSchema = z.object({
  input: z.union([IntellioCloseAssistantConversationSessionInputSchema, z.string()]),
}).strict();
export type IntellioCloseAssistantConversationSessionArguments = z.infer<typeof IntellioCloseAssistantConversationSessionArgumentsSchema>;

export const IntellioAssistantConversationMessageInputSchema = z.object({
  messageId: z.string().trim().min(1),
  role: z.string().trim().min(1),
  content: z.string(),
  citations: optionalNullishInput(JsonArraySchema),
  actions: optionalNullishInput(JsonArraySchema),
  metadata: optionalNullishInput(JsonObjectSchema),
  source: optionalNullishInput(IntellioAssistantConversationMessageSourceSchema),
  threadId: OptionalNonEmptyStringInput,
  runId: OptionalNonEmptyStringInput,
  workItemId: OptionalNonEmptyStringInput,
  createdAt: optionalNullishInput(UnixTimestamp),
}).strict();
export type IntellioAssistantConversationMessageInput = z.infer<typeof IntellioAssistantConversationMessageInputSchema>;

export const IntellioAppendAssistantConversationMessagesInputSchema = z.object({
  actorId: OptionalNonEmptyStringInput,
  sessionId: z.string().trim().min(1),
  messages: z.array(IntellioAssistantConversationMessageInputSchema).min(1),
}).strict();
export type IntellioAppendAssistantConversationMessagesInput = z.infer<typeof IntellioAppendAssistantConversationMessagesInputSchema>;

export const IntellioAppendAssistantConversationMessagesArgumentsSchema = z.object({
  input: z.union([IntellioAppendAssistantConversationMessagesInputSchema, z.string()]),
}).strict();
export type IntellioAppendAssistantConversationMessagesArguments = z.infer<typeof IntellioAppendAssistantConversationMessagesArgumentsSchema>;

export const IntellioAssistantConversationSessionMutationResultSchema = z.object({
  session: IntellioAssistantConversationSessionRecordSchema,
  appendedMessages: z.array(IntellioAssistantConversationMessageRecordSchema).optional(),
}).strict();
export type IntellioAssistantConversationSessionMutationResult = z.infer<typeof IntellioAssistantConversationSessionMutationResultSchema>;

export const IntellioAssistantSessionStateArgumentsSchema = z.object({
  actorId: OptionalNonEmptyStringInput,
  runId: OptionalNonEmptyStringInput,
  pathname: OptionalNonEmptyStringInput,
  module: optionalNullishInput(IntellioAssistantModuleSchema),
}).strict();
export type IntellioAssistantSessionStateArguments = z.infer<typeof IntellioAssistantSessionStateArgumentsSchema>;

export const IntellioAssistantSessionStateOutputSchema = z.object({
  activeRun: IntellioAssistantRunRecordSchema.nullable().optional(),
  workItems: z.array(IntellioAssistantWorkItemRecordSchema),
  totalCount: z.number().int().nonnegative(),
}).strict();
export type IntellioAssistantSessionStateOutput = z.infer<typeof IntellioAssistantSessionStateOutputSchema>;

export const IntellioAssistantWorkflowApprovalRecordSchema = z.object({
  id: z.string(),
  workflowDefinitionId: z.string(),
  status: AssistantWorkflowApprovalStatusSchema,
  reviewerActorId: z.string().nullable().optional(),
  reviewerActorRole: z.string().nullable().optional(),
  comment: z.string().nullable().optional(),
  autonomousDelivery: z.boolean(),
  reviewedAt: UnixTimestamp.nullable().optional(),
  createdAt: UnixTimestamp,
  updatedAt: UnixTimestamp,
}).strict();
export type IntellioAssistantWorkflowApprovalRecord = z.infer<typeof IntellioAssistantWorkflowApprovalRecordSchema>;

export const IntellioAssistantWorkflowTemplateRecordSchema = z.object({
  id: AssistantWorkflowTemplateIdSchema,
  title: z.string(),
  summary: z.string(),
  module: z.literal('admissions'),
  routeScope: z.string(),
  curatorOwned: z.boolean(),
  supportedTriggers: z.array(AssistantWorkflowTriggerKindSchema),
  supportedConnectors: z.array(z.enum(['email', 'google_sheet', 'google_drive'])),
  nodeCatalog: z.array(z.enum([
    'manual',
    'schedule',
    'admissions_threshold',
    'dataset',
    'filter',
    'select',
    'group',
    'aggregate',
    'branch',
    'spreadsheet',
    'chart',
    'email',
    'google_sheet',
    'google_drive',
  ])),
  defaultGraphSpec: AssistantWorkflowGraphSpecSchema,
  mermaidSpec: z.string().nullable().optional(),
  requiresModeration: z.boolean(),
  allowsAutonomousDelivery: z.boolean(),
  createdAt: UnixTimestamp.nullable().optional(),
  updatedAt: UnixTimestamp.nullable().optional(),
}).strict();
export type IntellioAssistantWorkflowTemplateRecord = z.infer<typeof IntellioAssistantWorkflowTemplateRecordSchema>;

export const IntellioAssistantWorkflowDefinitionRecordSchema = z.object({
  id: z.string(),
  templateId: AssistantWorkflowTemplateIdSchema,
  title: z.string(),
  description: z.string().nullable().optional(),
  creatorActorId: z.string(),
  creatorActorRole: z.string().nullable().optional(),
  tenantId: z.string(),
  tenantScopeStatus: IntellioAssistantTenantScopeStatusSchema.optional(),
  module: z.literal('admissions'),
  routeScope: z.string(),
  graphSpec: AssistantWorkflowGraphSpecSchema,
  mermaidSpec: z.string(),
  datasourceSelectionPlan: AssistantWorkflowDatasourceSelectionPlanSchema,
  connectorBindings: z.array(AssistantWorkflowConnectorBindingSchema),
  triggerConfig: AssistantWorkflowTriggerConfigSchema,
  approvalStatus: AssistantWorkflowApprovalStatusSchema,
  autonomousDelivery: z.boolean(),
  timezone: z.string(),
  executionSnapshotHash: z.string(),
  compiledAslJson: z.string(),
  compiledExecutionPackage: JsonObjectSchema,
  requiredOauthScopes: z.array(z.string()),
  latestApproval: IntellioAssistantWorkflowApprovalRecordSchema.nullable().optional(),
  lastSubmittedAt: UnixTimestamp.nullable().optional(),
  lastReviewedAt: UnixTimestamp.nullable().optional(),
  lastCompiledAt: UnixTimestamp.nullable().optional(),
  createdAt: UnixTimestamp,
  updatedAt: UnixTimestamp,
}).strict();
export type IntellioAssistantWorkflowDefinitionRecord = z.infer<typeof IntellioAssistantWorkflowDefinitionRecordSchema>;

export const IntellioAssistantWorkflowExecutionRecordSchema = z.object({
  id: z.string(),
  workflowDefinitionId: z.string(),
  creatorActorId: z.string(),
  tenantId: z.string(),
  module: z.literal('admissions'),
  route: z.string(),
  runId: z.string().nullable().optional(),
  workflowKey: z.string().nullable().optional(),
  triggerKind: AssistantWorkflowTriggerKindSchema,
  triggerContext: JsonObjectSchema.nullable().optional(),
  status: AssistantWorkflowExecutionStatusSchema,
  approvalRequired: z.boolean(),
  executionSnapshotHash: z.string(),
  stateMachineExecutionArn: z.string().nullable().optional(),
  blockedReason: z.string().nullable().optional(),
  resultSummary: z.string().nullable().optional(),
  errorMessage: z.string().nullable().optional(),
  artifactRefs: z.array(IntellioAssistantArtifactRefSchema),
  createdAt: UnixTimestamp,
  startedAt: UnixTimestamp.nullable().optional(),
  completedAt: UnixTimestamp.nullable().optional(),
  updatedAt: UnixTimestamp,
}).strict();
export type IntellioAssistantWorkflowExecutionRecord = z.infer<typeof IntellioAssistantWorkflowExecutionRecordSchema>;

export const IntellioAssistantWorkflowStudioModeSchema = z.enum(['new', 'existing']);
export type IntellioAssistantWorkflowStudioMode = z.infer<typeof IntellioAssistantWorkflowStudioModeSchema>;

export const IntellioAssistantWorkflowStudioViewerAuthoritySchema = z.object({
  actorId: z.string().nullable().optional(),
  actorRole: z.string().nullable().optional(),
  canEdit: z.boolean(),
  canReview: z.boolean(),
  canApprove: z.boolean(),
  canRun: z.boolean(),
}).strict();
export type IntellioAssistantWorkflowStudioViewerAuthority = z.infer<typeof IntellioAssistantWorkflowStudioViewerAuthoritySchema>;

export const IntellioAssistantWorkflowStudioConnectorStateSchema = z.object({
  status: z.enum(['ready', 'needs_attention', 'not_connected', 'download_only']),
  connected: z.boolean(),
  ready: z.boolean(),
  grantedScopes: z.array(z.string()),
  requiredScopes: z.array(z.string()),
  missingScopes: z.array(z.string()),
  lastVerifiedAt: UnixTimestamp.nullable().optional(),
  message: z.string().nullable().optional(),
}).strict();
export type IntellioAssistantWorkflowStudioConnectorState = z.infer<typeof IntellioAssistantWorkflowStudioConnectorStateSchema>;

export const IntellioAssistantWorkflowStudioConnectorReadinessSchema = z.object({
  email: IntellioAssistantWorkflowStudioConnectorStateSchema,
  googleWorkspace: IntellioAssistantWorkflowStudioConnectorStateSchema,
}).strict();
export type IntellioAssistantWorkflowStudioConnectorReadiness = z.infer<typeof IntellioAssistantWorkflowStudioConnectorReadinessSchema>;

export const IntellioAssistantWorkflowStudioTriggerHealthSchema = z.object({
  triggerKind: AssistantWorkflowTriggerKindSchema,
  counter: z.number().int().nonnegative(),
  windowDescription: z.string().nullable().optional(),
  lastTriggeredAt: UnixTimestamp.nullable().optional(),
  nextScheduledAt: UnixTimestamp.nullable().optional(),
  autoDeliveryState: z.enum(['manual_only', 'approval_required', 'enabled']),
}).strict();
export type IntellioAssistantWorkflowStudioTriggerHealth = z.infer<typeof IntellioAssistantWorkflowStudioTriggerHealthSchema>;

export const IntellioAssistantWorkflowStudioCanonicalThreadRefSchema = z.object({
  proofRoute: z.string().trim().min(1),
  launchRoute: z.string().trim().min(1),
  launchQuestion: z.string().trim().min(1),
  latestExecutionId: z.string().nullable().optional(),
  latestExecutionStatus: AssistantWorkflowExecutionStatusSchema.nullable().optional(),
  latestRunId: z.string().nullable().optional(),
}).strict();
export type IntellioAssistantWorkflowStudioCanonicalThreadRef = z.infer<typeof IntellioAssistantWorkflowStudioCanonicalThreadRefSchema>;

export const IntellioAssistantWorkflowStudioDefaultDraftSchema = z.object({
  title: z.string().trim().min(1),
  routeScope: z.string().trim().min(1),
  timezone: z.string().trim().min(1),
  triggerConfig: AssistantWorkflowTriggerConfigSchema,
}).strict();
export type IntellioAssistantWorkflowStudioDefaultDraft = z.infer<typeof IntellioAssistantWorkflowStudioDefaultDraftSchema>;

export const IntellioAssistantWorkflowTriggerStateRecordSchema = z.object({
  id: z.string(),
  workflowDefinitionId: z.string(),
  triggerKind: AssistantWorkflowTriggerKindSchema,
  metricKey: z.string(),
  counter: z.number().int().nonnegative(),
  threshold: z.number().int().positive().nullable().optional(),
  windowStartedAt: UnixTimestamp.nullable().optional(),
  lastObservedAt: UnixTimestamp.nullable().optional(),
  lastTriggeredAt: UnixTimestamp.nullable().optional(),
  metadata: JsonObjectSchema.nullable().optional(),
  createdAt: UnixTimestamp,
  updatedAt: UnixTimestamp,
}).strict();
export type IntellioAssistantWorkflowTriggerStateRecord = z.infer<typeof IntellioAssistantWorkflowTriggerStateRecordSchema>;

export const IntellioAssistantWorkflowTemplateConnectionSchema = z.object({
  items: z.array(IntellioAssistantWorkflowTemplateRecordSchema),
  totalCount: z.number().int().nonnegative(),
}).strict();
export type IntellioAssistantWorkflowTemplateConnection = z.infer<typeof IntellioAssistantWorkflowTemplateConnectionSchema>;

export const IntellioAssistantWorkflowConnectionSchema = z.object({
  items: z.array(IntellioAssistantWorkflowDefinitionRecordSchema),
  totalCount: z.number().int().nonnegative(),
  nextToken: z.string().nullable().optional(),
}).strict();
export type IntellioAssistantWorkflowConnection = z.infer<typeof IntellioAssistantWorkflowConnectionSchema>;

export const IntellioAssistantWorkflowExecutionConnectionSchema = z.object({
  items: z.array(IntellioAssistantWorkflowExecutionRecordSchema),
  totalCount: z.number().int().nonnegative(),
  nextToken: z.string().nullable().optional(),
}).strict();
export type IntellioAssistantWorkflowExecutionConnection = z.infer<typeof IntellioAssistantWorkflowExecutionConnectionSchema>;

export const IntellioAssistantWorkflowMutationResultSchema = z.object({
  workflow: IntellioAssistantWorkflowDefinitionRecordSchema,
  approval: IntellioAssistantWorkflowApprovalRecordSchema.nullable().optional(),
}).strict();
export type IntellioAssistantWorkflowMutationResult = z.infer<typeof IntellioAssistantWorkflowMutationResultSchema>;

export const IntellioAssistantReportTemplateRecordSchema = IntellioAssistantReportTemplateSchema;
export type IntellioAssistantReportTemplateRecord = z.infer<typeof IntellioAssistantReportTemplateRecordSchema>;

export const IntellioAssistantReportScheduleRecordSchema = IntellioAssistantReportScheduleSchema;
export type IntellioAssistantReportScheduleRecord = z.infer<typeof IntellioAssistantReportScheduleRecordSchema>;

export const IntellioAssistantReportPreviewOutputSchema = AssistantReportPreviewSchema;
export type IntellioAssistantReportPreviewOutput = z.infer<typeof IntellioAssistantReportPreviewOutputSchema>;

export const IntellioAssistantWorkflowStudioStateOutputSchema = z.object({
  mode: IntellioAssistantWorkflowStudioModeSchema,
  workflowId: z.string().trim().min(1),
  workflow: IntellioAssistantWorkflowDefinitionRecordSchema.nullable().optional(),
  templates: z.array(IntellioAssistantWorkflowTemplateRecordSchema),
  executions: z.array(IntellioAssistantWorkflowExecutionRecordSchema),
  executionsNextToken: z.string().nullable().optional(),
  viewerAuthority: IntellioAssistantWorkflowStudioViewerAuthoritySchema,
  connectorReadiness: IntellioAssistantWorkflowStudioConnectorReadinessSchema,
  triggerHealth: IntellioAssistantWorkflowStudioTriggerHealthSchema,
  canonicalThread: IntellioAssistantWorkflowStudioCanonicalThreadRefSchema,
  defaultDraft: IntellioAssistantWorkflowStudioDefaultDraftSchema.nullable().optional(),
}).strict();
export type IntellioAssistantWorkflowStudioStateOutput = z.infer<typeof IntellioAssistantWorkflowStudioStateOutputSchema>;

export const IntellioAssistantReportTemplateListInputSchema = z.object({
  pathname: OptionalNonEmptyStringInput,
  includeShared: optionalNullishInput(z.boolean()),
  includeArchived: optionalNullishInput(z.boolean()),
  limit: optionalNullishInput(z.number().int().positive().max(100)),
  nextToken: OptionalNonEmptyStringInput,
}).strict();
export type IntellioAssistantReportTemplateListInput = z.infer<typeof IntellioAssistantReportTemplateListInputSchema>;

export const IntellioAssistantReportTemplateListArgumentsSchema = z.object({
  input: z.union([IntellioAssistantReportTemplateListInputSchema, z.string()]).optional(),
}).strict();
export type IntellioAssistantReportTemplateListArguments = z.infer<typeof IntellioAssistantReportTemplateListArgumentsSchema>;

export const IntellioAssistantReportTemplateConnectionSchema = z.object({
  items: z.array(IntellioAssistantReportTemplateRecordSchema),
  totalCount: z.number().int().nonnegative(),
  nextToken: z.string().nullable().optional(),
}).strict();
export type IntellioAssistantReportTemplateConnection = z.infer<typeof IntellioAssistantReportTemplateConnectionSchema>;

export const IntellioAssistantReportScheduleListInputSchema = z.object({
  includeDisabled: optionalNullishInput(z.boolean()),
  limit: optionalNullishInput(z.number().int().positive().max(100)),
  nextToken: OptionalNonEmptyStringInput,
}).strict();
export type IntellioAssistantReportScheduleListInput = z.infer<typeof IntellioAssistantReportScheduleListInputSchema>;

export const IntellioAssistantReportScheduleListArgumentsSchema = z.object({
  input: z.union([IntellioAssistantReportScheduleListInputSchema, z.string()]).optional(),
}).strict();
export type IntellioAssistantReportScheduleListArguments = z.infer<typeof IntellioAssistantReportScheduleListArgumentsSchema>;

export const IntellioAssistantReportScheduleConnectionSchema = z.object({
  items: z.array(IntellioAssistantReportScheduleRecordSchema),
  totalCount: z.number().int().nonnegative(),
  nextToken: z.string().nullable().optional(),
}).strict();
export type IntellioAssistantReportScheduleConnection = z.infer<typeof IntellioAssistantReportScheduleConnectionSchema>;

export const IntellioAssistantReportPreviewInputSchema = z.object({
  pathname: OptionalNonEmptyStringInput,
  module: optionalNullishInput(IntellioAssistantModuleSchema),
  reportDefinition: AssistantReportDefinitionSchema,
  limit: optionalNullishInput(z.number().int().positive().max(500)),
}).strict();
export type IntellioAssistantReportPreviewInput = z.infer<typeof IntellioAssistantReportPreviewInputSchema>;

export const IntellioAssistantReportPreviewArgumentsSchema = z.object({
  input: z.union([IntellioAssistantReportPreviewInputSchema, z.string()]),
}).strict();
export type IntellioAssistantReportPreviewArguments = z.infer<typeof IntellioAssistantReportPreviewArgumentsSchema>;

export const IntellioAssistantReportGenerationWorkspaceInputSchema = z.object({
  pathname: OptionalNonEmptyStringInput,
  module: optionalNullishInput(IntellioAssistantModuleSchema),
  reportDefinition: AssistantReportDefinitionSchema,
  previewLimit: optionalNullishInput(z.number().int().positive().max(500)),
  templateLimit: optionalNullishInput(z.number().int().positive().max(100)),
  scheduleLimit: optionalNullishInput(z.number().int().positive().max(100)),
  workItemLimit: optionalNullishInput(z.number().int().positive().max(100)),
  templatesNextToken: OptionalNonEmptyStringInput,
  schedulesNextToken: OptionalNonEmptyStringInput,
  historyNextToken: OptionalNonEmptyStringInput,
  includeSharedTemplates: optionalNullishInput(z.boolean()),
  includeArchivedTemplates: optionalNullishInput(z.boolean()),
  includeDisabledSchedules: optionalNullishInput(z.boolean()),
  includeClosedWorkItems: optionalNullishInput(z.boolean()),
}).strict();
export type IntellioAssistantReportGenerationWorkspaceInput = z.infer<typeof IntellioAssistantReportGenerationWorkspaceInputSchema>;

export const IntellioAssistantReportGenerationWorkspaceArgumentsSchema = z.object({
  input: z.union([IntellioAssistantReportGenerationWorkspaceInputSchema, z.string()]),
}).strict();
export type IntellioAssistantReportGenerationWorkspaceArguments = z.infer<typeof IntellioAssistantReportGenerationWorkspaceArgumentsSchema>;

export const IntellioMutateAssistantReportTemplateInputSchema = z.object({
  action: z.enum(['upsert', 'archive', 'delete']),
  templateId: OptionalNonEmptyStringInput,
  visibility: optionalNullishInput(z.enum(['private', 'shared'])),
  title: OptionalNonEmptyStringInput,
  description: OptionalNonEmptyStringInput,
  sourceTemplateId: OptionalNonEmptyStringInput,
  reportDefinition: optionalNullishInput(AssistantReportDefinitionSchema),
}).strict();
export type IntellioMutateAssistantReportTemplateInput = z.infer<typeof IntellioMutateAssistantReportTemplateInputSchema>;

export const IntellioMutateAssistantReportTemplateArgumentsSchema = z.object({
  input: z.union([IntellioMutateAssistantReportTemplateInputSchema, z.string()]),
}).strict();
export type IntellioMutateAssistantReportTemplateArguments = z.infer<typeof IntellioMutateAssistantReportTemplateArgumentsSchema>;

export const IntellioMutateAssistantReportTemplateOutputSchema = z.object({
  accepted: z.boolean(),
  template: IntellioAssistantReportTemplateRecordSchema.nullable().optional(),
}).strict();
export type IntellioMutateAssistantReportTemplateOutput = z.infer<typeof IntellioMutateAssistantReportTemplateOutputSchema>;

export const IntellioMutateAssistantReportScheduleInputSchema = z.object({
  action: z.enum(['upsert', 'delete', 'toggle']),
  scheduleId: OptionalNonEmptyStringInput,
  title: OptionalNonEmptyStringInput,
  reportTemplateId: OptionalNonEmptyStringInput,
  reportDefinition: optionalNullishInput(AssistantReportDefinitionSchema),
  exportFormats: optionalNullishInput(z.array(AssistantReportExportFormatSchema)),
  scheduleExpression: OptionalNonEmptyStringInput,
  timezone: OptionalNonEmptyStringInput,
  enabled: optionalNullishInput(z.boolean()),
}).strict();
export type IntellioMutateAssistantReportScheduleInput = z.infer<typeof IntellioMutateAssistantReportScheduleInputSchema>;

export const IntellioMutateAssistantReportScheduleArgumentsSchema = z.object({
  input: z.union([IntellioMutateAssistantReportScheduleInputSchema, z.string()]),
}).strict();
export type IntellioMutateAssistantReportScheduleArguments = z.infer<typeof IntellioMutateAssistantReportScheduleArgumentsSchema>;

export const IntellioMutateAssistantReportScheduleOutputSchema = z.object({
  accepted: z.boolean(),
  schedule: IntellioAssistantReportScheduleRecordSchema.nullable().optional(),
}).strict();
export type IntellioMutateAssistantReportScheduleOutput = z.infer<typeof IntellioMutateAssistantReportScheduleOutputSchema>;

export const IntellioAssistantWorkflowTemplateQueryInputSchema = z.object({
  pathname: OptionalNonEmptyStringInput,
  module: optionalNullishInput(IntellioAssistantModuleSchema),
}).strict();
export type IntellioAssistantWorkflowTemplateQueryInput = z.infer<typeof IntellioAssistantWorkflowTemplateQueryInputSchema>;

export const IntellioAssistantWorkflowTemplateQueryArgumentsSchema = z.object({
  input: z.union([IntellioAssistantWorkflowTemplateQueryInputSchema, z.string()]).optional(),
}).strict();
export type IntellioAssistantWorkflowTemplateQueryArguments = z.infer<typeof IntellioAssistantWorkflowTemplateQueryArgumentsSchema>;

export const IntellioAssistantWorkflowListInputSchema = z.object({
  actorId: OptionalNonEmptyStringInput,
  includeAllTenant: optionalNullishInput(z.boolean()),
  approvalStatus: optionalNullishInput(AssistantWorkflowApprovalStatusSchema),
  routeScope: OptionalNonEmptyStringInput,
  limit: optionalNullishInput(z.number().int().positive().max(100)),
  nextToken: OptionalNonEmptyStringInput,
}).strict();
export type IntellioAssistantWorkflowListInput = z.infer<typeof IntellioAssistantWorkflowListInputSchema>;

export const IntellioAssistantWorkflowListArgumentsSchema = z.object({
  input: z.union([IntellioAssistantWorkflowListInputSchema, z.string()]).optional(),
}).strict();
export type IntellioAssistantWorkflowListArguments = z.infer<typeof IntellioAssistantWorkflowListArgumentsSchema>;

export const IntellioAssistantWorkflowGetArgumentsSchema = z.object({
  id: z.string().trim().min(1),
}).strict();
export type IntellioAssistantWorkflowGetArguments = z.infer<typeof IntellioAssistantWorkflowGetArgumentsSchema>;

export const IntellioUpsertAssistantWorkflowDraftInputSchema = z.object({
  actorId: OptionalNonEmptyStringInput,
  workflowId: OptionalNonEmptyStringInput,
  templateId: AssistantWorkflowTemplateIdSchema,
  title: z.string().trim().min(1),
  description: OptionalNonEmptyStringInput,
  routeScope: z.string().trim().min(1),
  graphSpec: AssistantWorkflowGraphSpecSchema,
  connectorBindings: optionalNullishInput(z.array(AssistantWorkflowConnectorBindingSchema)),
  triggerConfig: AssistantWorkflowTriggerConfigSchema,
  timezone: z.string().trim().min(1),
  autonomousDelivery: optionalNullishInput(z.boolean()),
}).strict();
export type IntellioUpsertAssistantWorkflowDraftInput = z.infer<typeof IntellioUpsertAssistantWorkflowDraftInputSchema>;

export const IntellioUpsertAssistantWorkflowDraftArgumentsSchema = z.object({
  input: z.union([IntellioUpsertAssistantWorkflowDraftInputSchema, z.string()]),
}).strict();
export type IntellioUpsertAssistantWorkflowDraftArguments = z.infer<typeof IntellioUpsertAssistantWorkflowDraftArgumentsSchema>;

export const IntellioSubmitAssistantWorkflowForApprovalInputSchema = z.object({
  actorId: OptionalNonEmptyStringInput,
  workflowDefinitionId: z.string().trim().min(1),
}).strict();
export type IntellioSubmitAssistantWorkflowForApprovalInput = z.infer<typeof IntellioSubmitAssistantWorkflowForApprovalInputSchema>;

export const IntellioSubmitAssistantWorkflowForApprovalArgumentsSchema = z.object({
  input: z.union([IntellioSubmitAssistantWorkflowForApprovalInputSchema, z.string()]),
}).strict();
export type IntellioSubmitAssistantWorkflowForApprovalArguments = z.infer<typeof IntellioSubmitAssistantWorkflowForApprovalArgumentsSchema>;

export const IntellioReviewAssistantWorkflowInputSchema = z.object({
  actorId: OptionalNonEmptyStringInput,
  workflowDefinitionId: z.string().trim().min(1),
  decision: AssistantWorkflowReviewDecisionSchema,
  comment: OptionalNonEmptyStringInput,
  autonomousDelivery: optionalNullishInput(z.boolean()),
}).strict();
export type IntellioReviewAssistantWorkflowInput = z.infer<typeof IntellioReviewAssistantWorkflowInputSchema>;

export const IntellioReviewAssistantWorkflowArgumentsSchema = z.object({
  input: z.union([IntellioReviewAssistantWorkflowInputSchema, z.string()]),
}).strict();
export type IntellioReviewAssistantWorkflowArguments = z.infer<typeof IntellioReviewAssistantWorkflowArgumentsSchema>;

export const IntellioAssistantWorkflowExecutionListInputSchema = z.object({
  workflowDefinitionId: z.string().trim().min(1),
  limit: optionalNullishInput(z.number().int().positive().max(100)),
  nextToken: OptionalNonEmptyStringInput,
}).strict();
export type IntellioAssistantWorkflowExecutionListInput = z.infer<typeof IntellioAssistantWorkflowExecutionListInputSchema>;

export const IntellioAssistantWorkflowExecutionListArgumentsSchema = z.object({
  input: z.union([IntellioAssistantWorkflowExecutionListInputSchema, z.string()]).optional(),
}).strict();
export type IntellioAssistantWorkflowExecutionListArguments = z.infer<typeof IntellioAssistantWorkflowExecutionListArgumentsSchema>;

export const IntellioAssistantWorkflowExecutionGetArgumentsSchema = z.object({
  id: z.string().trim().min(1),
}).strict();
export type IntellioAssistantWorkflowExecutionGetArguments = z.infer<typeof IntellioAssistantWorkflowExecutionGetArgumentsSchema>;

export const IntellioAssistantWorkflowStudioStateArgumentsSchema = z.object({
  workflowId: z.string().trim().min(1),
  pathname: OptionalNonEmptyStringInput,
}).strict();
export type IntellioAssistantWorkflowStudioStateArguments = z.infer<typeof IntellioAssistantWorkflowStudioStateArgumentsSchema>;

export const IntellioAssistantWorkItemsQueryInputSchema = z.object({
  actorId: OptionalNonEmptyStringInput,
  includeClosed: optionalNullishInput(z.boolean()),
  module: optionalNullishInput(IntellioAssistantModuleSchema),
  pathname: OptionalNonEmptyStringInput,
  limit: optionalNullishInput(z.number().int().positive().max(100)),
  nextToken: OptionalNonEmptyStringInput,
}).strict();
export type IntellioAssistantWorkItemsQueryInput = z.infer<typeof IntellioAssistantWorkItemsQueryInputSchema>;

export const IntellioAssistantWorkItemsQueryArgumentsSchema = z.object({
  input: z.union([IntellioAssistantWorkItemsQueryInputSchema, z.string()]).optional(),
}).strict();
export type IntellioAssistantWorkItemsQueryArguments = z.infer<typeof IntellioAssistantWorkItemsQueryArgumentsSchema>;

export const IntellioAssistantWorkItemsQueryOutputSchema = z.object({
  items: z.array(IntellioAssistantWorkItemRecordSchema),
  totalCount: z.number().int().nonnegative(),
  nextToken: z.string().nullable().optional(),
}).strict();
export type IntellioAssistantWorkItemsQueryOutput = z.infer<typeof IntellioAssistantWorkItemsQueryOutputSchema>;

export const IntellioAssistantReportGenerationWorkspaceMetricsSchema = z.object({
  queryShapeVersion: z.literal('run-reports-workspace-v1'),
  appSyncOperationBudget: z.number().int().positive(),
  serverSideOperationBudget: z.number().int().positive(),
  previewLimit: z.number().int().positive(),
  templateLimit: z.number().int().positive(),
  scheduleLimit: z.number().int().positive(),
  workItemLimit: z.number().int().positive(),
}).strict();
export type IntellioAssistantReportGenerationWorkspaceMetrics = z.infer<typeof IntellioAssistantReportGenerationWorkspaceMetricsSchema>;

export const IntellioAssistantReportGenerationWorkspaceOutputSchema = z.object({
  preview: IntellioAssistantReportPreviewOutputSchema,
  templates: IntellioAssistantReportTemplateConnectionSchema,
  schedules: IntellioAssistantReportScheduleConnectionSchema,
  history: IntellioAssistantWorkItemsQueryOutputSchema,
  metrics: IntellioAssistantReportGenerationWorkspaceMetricsSchema,
}).strict();
export type IntellioAssistantReportGenerationWorkspaceOutput = z.infer<typeof IntellioAssistantReportGenerationWorkspaceOutputSchema>;

export const IntellioMutateAssistantRunInputSchema = z.object({
  action: z.enum(['start', 'cancel', 'confirm']),
  actorId: OptionalNonEmptyStringInput,
  question: OptionalNonEmptyStringInput,
  conversationHistory: optionalNullishInput(JsonArraySchema),
  context: optionalNullishInput(JsonObjectSchema),
  activeRouteContext: optionalNullishInput(AssistantActiveRouteContextEnvelopeSchema),
  model: OptionalNonEmptyStringInput,
  runtimePreferences: optionalNullishInput(JsonObjectSchema),
  routingHints: optionalNullishInput(IntellioRoutingHintsSchema),
  workItemId: OptionalNonEmptyStringInput,
  runId: OptionalNonEmptyStringInput,
  origin: optionalNullishInput(IntellioAssistantConversationOriginRefSchema),
}).strict();
export type IntellioMutateAssistantRunInput = z.infer<typeof IntellioMutateAssistantRunInputSchema>;

export const IntellioMutateAssistantRunArgumentsSchema = z.object({
  input: z.union([IntellioMutateAssistantRunInputSchema, z.string()]),
}).strict();
export type IntellioMutateAssistantRunArguments = z.infer<typeof IntellioMutateAssistantRunArgumentsSchema>;

export const IntellioMutateAssistantRunOutputSchema = z.object({
  accepted: z.boolean(),
  run: IntellioAssistantRunRecordSchema.nullable().optional(),
}).strict();
export type IntellioMutateAssistantRunOutput = z.infer<typeof IntellioMutateAssistantRunOutputSchema>;

export const IntellioMutateAssistantWorkItemsInputSchema = z.object({
  action: z.enum(['open', 'dismiss', 'snooze', 'complete']),
  actorId: OptionalNonEmptyStringInput,
  ids: z.array(z.string().trim().min(1)).min(1),
  snoozePreset: optionalNullishInput(IntellioAssistantSnoozePresetSchema),
}).strict();
export type IntellioMutateAssistantWorkItemsInput = z.infer<typeof IntellioMutateAssistantWorkItemsInputSchema>;

export const IntellioMutateAssistantWorkItemsArgumentsSchema = z.object({
  input: z.union([IntellioMutateAssistantWorkItemsInputSchema, z.string()]),
}).strict();
export type IntellioMutateAssistantWorkItemsArguments = z.infer<typeof IntellioMutateAssistantWorkItemsArgumentsSchema>;

export const IntellioMutateAssistantWorkItemsOutputSchema = z.object({
  items: z.array(IntellioAssistantWorkItemRecordSchema),
  changedCount: z.number().int().nonnegative(),
  updatedAt: UnixTimestamp,
}).strict();
export type IntellioMutateAssistantWorkItemsOutput = z.infer<typeof IntellioMutateAssistantWorkItemsOutputSchema>;

export const IntellioProactiveDispatchPayloadSchema = z.object({
  title: z.string().trim().min(1),
  body: z.string().trim().min(1),
  module: IntellioAssistantModuleSchema.default('general'),
  route: z.string().trim().min(1),
  sourceRef: z.string().trim().min(1),
  actorId: OptionalNonEmptyStringInput,
  familyId: OptionalNonEmptyStringInput,
  tenantId: OptionalNonEmptyStringInput,
  traceId: OptionalNonEmptyStringInput,
  assistantPrompt: AssistantPromptPayloadSchema.nullable().optional(),
  priority: optionalNullishInput(IntellioInterventionPrioritySchema),
  channels: optionalNullishInput(z.array(IntellioProactiveChannelSchema)),
  recipients: optionalNullishInput(z.array(z.string().trim().min(1))),
  metadata: optionalNullishInput(JsonObjectSchema),
}).strict();
export type IntellioProactiveDispatchPayload = z.infer<typeof IntellioProactiveDispatchPayloadSchema>;

export const IntellioDirectProactiveDispatchEventSchema = z.object({
  proactivePayload: IntellioProactiveDispatchPayloadSchema.optional(),
}).strict();
export type IntellioDirectProactiveDispatchEvent = z.infer<typeof IntellioDirectProactiveDispatchEventSchema>;
